import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../styles/theme';

const Page = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.background.primary};
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  /* Override global 12px html font on mobile - chat needs readable text */
  font-size: 16px;
  /* Safe area for notched phones */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  /* Prevent overscroll and horizontal scroll */
  overflow: hidden;
  overflow-x: hidden;
`;

const Header = styled.header`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.background.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
  min-height: 52px;
  min-width: 0;
  overflow: hidden;

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    min-height: 56px;
  }
`;

const BackLink = styled(Link)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  font-size: 17px;
  padding: ${theme.spacing.sm} 0;
  min-width: 44px;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
  &:hover, &:focus {
    color: ${theme.colors.text.primary};
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  flex: 1;
  min-width: 0;
  justify-content: center;

  @media (min-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.lg};
    justify-content: flex-start;
  }
`;

const TabsStrip = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 ${theme.spacing.md} ${theme.spacing.sm};
  flex-shrink: 0;
`;

const TabsRow = styled.div`
  display: flex;
  gap: 2px;
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.lg};
  padding: 2px;
  max-width: 320px;
`;

const Tab = styled.button`
  flex: 1;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${(p) => (p.$active ? theme.colors.primary.main : 'transparent')};
  color: ${(p) => (p.$active ? theme.colors.primary.contrast : theme.colors.text.secondary)};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium)};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s, color 0.15s;
  &:hover:not([disabled]) {
    color: ${(p) => (p.$active ? theme.colors.primary.contrast : theme.colors.text.primary)};
    background: ${(p) => (p.$active ? theme.colors.primary.main : 'rgba(255,255,255,0.06)')};
  }
`;

const VesselChip = styled.button`
  flex-shrink: 0;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.full};
  color: ${theme.colors.text.secondary};
  font-size: 14px;
  cursor: pointer;
  min-height: 36px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
  &:hover {
    border-color: ${theme.colors.primary.light};
    color: ${theme.colors.text.primary};
  }

  @media (min-width: ${theme.breakpoints.sm}) {
    max-width: 160px;
    min-height: 40px;
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.lg};
  }
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${(p) => (p.$isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
  flex-shrink: 0;
  min-width: 0;
`;

const MessageBubble = styled.div`
  max-width: 90%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  font-size: 17px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  background: ${(p) =>
    p.$isUser ? theme.colors.primary.main : theme.colors.background.card};
  color: ${(p) => (p.$isUser ? theme.colors.primary.contrast : theme.colors.text.primary)};
  border: ${(p) => (p.$isUser ? 'none' : `1px solid ${theme.colors.border.primary}`)};
  min-height: 44px;
  position: relative;
  z-index: 1;

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    border-radius: ${theme.borderRadius.xl};
    max-width: 88%;
    font-size: 1rem;
  }
`;

const MessageTime = styled.span`
  font-size: 13px;
  color: ${(p) => (p.$isUser ? 'rgba(255,255,255,0.7)' : theme.colors.text.disabled)};
  margin-top: 4px;
  display: block;
`;

const InputArea = styled.div`
  flex-shrink: 0;
  width: 100%;
  max-width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  padding-bottom: calc(${theme.spacing.md} + env(safe-area-inset-bottom));
  background: ${theme.colors.background.secondary};
  border-top: 1px solid ${theme.colors.border.primary};
  position: relative;
  box-sizing: border-box;

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
  }
`;

const InputRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: flex-end;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const Input = styled.textarea`
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  min-height: 44px;
  max-height: 120px;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.input.background};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: 17px;
  font-family: inherit;
  resize: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.light};
  }
  &::placeholder {
    color: ${theme.colors.text.disabled};
  }

  @media (min-width: ${theme.breakpoints.sm}) {
    min-height: 48px;
    max-height: 140px;
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    border-radius: ${theme.borderRadius.xl};
    font-size: 16px;
  }
`;

const SendButton = styled.button`
  width: 44px;
  height: 44px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%);
  color: ${theme.colors.primary.contrast};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: 1.25rem;
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  &:hover:not(:disabled) {
    opacity: 0.95;
  }
  &:active:not(:disabled) {
    transform: scale(0.96);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (min-width: ${theme.breakpoints.sm}) {
    width: 48px;
    height: 48px;
    border-radius: ${theme.borderRadius.xl};
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${theme.colors.text.secondary};
  font-size: 17px;
  line-height: 1.6;

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.xl};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const ErrorBanner = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid ${theme.colors.error.main};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.error.light};
  font-size: 15px;
  margin: 0 ${theme.spacing.md} ${theme.spacing.sm};
`;

const AlertsBanner = styled.div`
  padding: ${theme.spacing.md};
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid #d97706;
  border-radius: ${theme.borderRadius.md};
  color: #b45309;
  font-size: 15px;
  line-height: 1.5;
  margin-bottom: ${theme.spacing.md};
  flex-shrink: 0;

  @media (min-width: ${theme.breakpoints.sm}) {
    margin: 0 ${theme.spacing.lg} ${theme.spacing.md};
  }
`;

const VesselPicker = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: ${theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
`;

const VesselList = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl} ${theme.borderRadius.xl} 0 0;
  max-height: 60vh;
  overflow-y: auto;
  padding: ${theme.spacing.md};
  -webkit-overflow-scrolling: touch;

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.lg};
  }
`;

const VesselOption = styled.button`
  display: block;
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  min-height: 48px;
  background: none;
  border: none;
  color: ${theme.colors.text.primary};
  font-size: 17px;
  text-align: left;
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  -webkit-tap-highlight-color: transparent;
  &:hover, &:focus {
    background: ${theme.colors.background.card};
  }
`;

const ChatPage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [vessels, setVessels] = useState([]);
  const [selectedVesselIds, setSelectedVesselIds] = useState(null);
  const [showVesselPicker, setShowVesselPicker] = useState(false);
  const [mode, setMode] = useState('platform'); // 'platform' | 'expert'
  const [platformMessages, setPlatformMessages] = useState([]);
  const [expertMessages, setExpertMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [alerts, setAlerts] = useState({ recentAlerts: [], currentCheck: null });
  const [alertsLoading, setAlertsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const messages = mode === 'platform' ? platformMessages : expertMessages;
  const setMessages = mode === 'platform' ? setPlatformMessages : setExpertMessages;

  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      navigate('/login', { replace: true, state: { from: '/chat' } });
      return;
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setStatusLoaded(false);
    const load = async () => {
      try {
        const [vRes, sRes] = await Promise.all([
          api.get('/api/vessels'),
          api.get('/api/ai-assistant/status'),
        ]);
        const vList = vRes.data?.vessels || [];
        setVessels(vList);
        setConfigured(sRes.data?.configured ?? false);
      } catch (e) {
        setError('Could not load vessels');
      } finally {
        setStatusLoaded(true);
      }
    };
    load();
  }, [isAuthenticated]);

  // Check for current/outstanding alerts when chat loads (including friend requests, so run even with no vessels)
  useEffect(() => {
    if (!isAuthenticated) return;
    setAlertsLoading(true);
    const vesselIds = selectedVesselIds ? selectedVesselIds.join(',') : '';
    const params = new URLSearchParams({ checkCurrent: '1' });
    if (vesselIds) params.set('vesselIds', vesselIds);
    api.get(`/api/ai-assistant/alerts?${params}`)
      .then((res) => setAlerts(res.data || { recentAlerts: [], currentCheck: null, friendRequests: [], acceptedRequests: [] }))
      .catch(() => setAlerts({ recentAlerts: [], currentCheck: null, friendRequests: [], acceptedRequests: [] }))
      .finally(() => setAlertsLoading(false));
  }, [isAuthenticated, vessels.length, selectedVesselIds]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, mode]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading || !configured) return;

    setInput('');
    setLoading(true);
    setError(null);

    const userMsg = {
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);

    try {
      const res = await api.post('/api/ai-assistant/chat', {
        message: text,
        conversationId: null,
        vesselIds: mode === 'platform' ? selectedVesselIds : null,
        mode,
      });

      const assistantMsg = {
        role: 'assistant',
        content: res.data.content,
        createdAt: new Date().toISOString(),
      };
      setMessages((m) => [...m, assistantMsg]);
      // Refetch alerts so friend request banner updates after accept/decline
      const vesselIds = selectedVesselIds ? selectedVesselIds.join(',') : '';
      const params = new URLSearchParams({ checkCurrent: '0' });
      if (vesselIds) params.set('vesselIds', vesselIds);
      api.get(`/api/ai-assistant/alerts?${params}`)
        .then((data) => setAlerts(data.data || { recentAlerts: [], currentCheck: null, friendRequests: [], acceptedRequests: [] }))
        .catch(() => {});
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to send message');
      setMessages((m) => m.filter((msg) => msg.role !== 'user' || msg.content !== text));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const selectedVesselLabel = selectedVesselIds === null
    ? 'All my boats'
    : vessels.find((v) => v.id === selectedVesselIds?.[0])?.vessel_name || 'Select boat';

  if (authLoading || !isAuthenticated) {
    return (
      <Page>
        <EmptyState>Loading…</EmptyState>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <BackLink to="/">
          ← Back
        </BackLink>
        <Title>
          <span role="img" aria-label="boat">🚢</span>
          Boat Chat
        </Title>
        {mode === 'platform' && vessels.length > 1 ? (
          <VesselChip onClick={() => setShowVesselPicker(true)}>
            {selectedVesselLabel}
          </VesselChip>
        ) : (
          <div style={{ width: 48 }} />
        )}
      </Header>
      <TabsStrip>
        <TabsRow>
          <Tab
            $active={mode === 'platform'}
            onClick={() => setMode('platform')}
            aria-pressed={mode === 'platform'}
          >
            My Boat
          </Tab>
          <Tab
            $active={mode === 'expert'}
            onClick={() => setMode('expert')}
            aria-pressed={mode === 'expert'}
          >
            BoatWise Expert
          </Tab>
        </TabsRow>
      </TabsStrip>

      {showVesselPicker && (
        <VesselPicker onClick={() => setShowVesselPicker(false)}>
          <VesselList onClick={(e) => e.stopPropagation()}>
            <VesselOption
              onClick={() => {
                setSelectedVesselIds(null);
                setShowVesselPicker(false);
              }}
            >
              All my boats
            </VesselOption>
            {vessels.map((v) => (
              <VesselOption
                key={v.id}
                onClick={() => {
                  setSelectedVesselIds([v.id]);
                  setShowVesselPicker(false);
                }}
              >
                {v.vessel_name}
              </VesselOption>
            ))}
          </VesselList>
        </VesselPicker>
      )}

      {statusLoaded && !configured && (
        <ErrorBanner>
          AI assistant is not configured. Set OPENROUTER_API_KEY or OPENAI_API_KEY on the server to enable.
        </ErrorBanner>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <MessagesArea>
        {alertsLoading && (
          <AlertsBanner style={{ background: 'rgba(100,116,139,0.15)', borderColor: theme.colors.border.primary, color: theme.colors.text.secondary }}>
            Checking boat status…
          </AlertsBanner>
        )}
        {!alertsLoading && (alerts.recentAlerts?.length > 0 || alerts.currentCheck?.hasIssues) && (
          <AlertsBanner>
            <strong>Active alerts</strong>
            <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
              {alerts.currentCheck?.hasIssues
                ? alerts.currentCheck.summary
                : alerts.recentAlerts?.[0]?.summary}
            </p>
          </AlertsBanner>
        )}
        {!alertsLoading && (alerts.friendRequests?.length > 0 || alerts.acceptedRequests?.length > 0) && (
          <AlertsBanner style={{ background: 'rgba(34, 197, 94, 0.12)', borderColor: '#16a34a' }}>
            {alerts.friendRequests?.length > 0 && (
              <p style={{ margin: 0 }}>
                <strong>Friend requests</strong>
                {' '}
                {alerts.friendRequests.map((r) => [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email).join(', ')}
                {' '}
                {alerts.friendRequests.length === 1 ? 'wants' : 'want'} to be your friend. Respond in chat to accept or decline.
              </p>
            )}
            {alerts.acceptedRequests?.length > 0 && alerts.friendRequests?.length > 0 && <br />}
            {alerts.acceptedRequests?.length > 0 && (
              <p style={{ margin: alerts.friendRequests?.length > 0 ? '8px 0 0' : 0 }}>
                <strong>Accepted</strong>
                {' '}
                {alerts.acceptedRequests.map((r) => [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email).join(', ')}
                {' '}
                {alerts.acceptedRequests.length === 1 ? 'accepted' : 'accepted'} your friend request.
              </p>
            )}
          </AlertsBanner>
        )}
        {messages.length === 0 && (
          <EmptyState>
            {mode === 'platform' ? (
              <>
                <p>Ask anything about your boat.</p>
                <p style={{ marginTop: 12, fontSize: '15px', opacity: 0.9 }}>
                  e.g. &quot;Battery voltage?&quot; or &quot;Turn OnGuard on&quot;
                </p>
              </>
            ) : (
              <>
                <p>Ask about marine electronics, manuals, troubleshooting.</p>
                <p style={{ marginTop: 12, fontSize: '15px', opacity: 0.9 }}>
                  e.g. &quot;How do I calibrate my depth sounder?&quot; or &quot;Raymarine autopilot setup&quot;
                </p>
              </>
            )}
          </EmptyState>
        )}
        {messages.map((msg, i) => (
          <MessageRow key={i} $isUser={msg.role === 'user'}>
            <MessageBubble $isUser={msg.role === 'user'}>
              {msg.content}
              <MessageTime $isUser={msg.role === 'user'}>
                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </MessageTime>
            </MessageBubble>
          </MessageRow>
        ))}
        {loading && (
          <MessageRow $isUser={false}>
            <MessageBubble $isUser={false}>
              <em>Thinking…</em>
            </MessageBubble>
          </MessageRow>
        )}
        <div ref={messagesEndRef} />
      </MessagesArea>

      <InputArea>
        <InputRow>
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={mode === 'platform' ? 'Ask about your boat...' : 'Ask about equipment, manuals, troubleshooting...'}
            disabled={!configured || loading}
            rows={1}
          />
          <SendButton
            onClick={handleSend}
            disabled={!input.trim() || loading || !configured}
            aria-label="Send"
          >
            →
          </SendButton>
        </InputRow>
      </InputArea>
    </Page>
  );
};

export default ChatPage;
